"use client";

import {
  cn,
  formatPhoneNumber,
  getDayName,
  getPeriodTime,
} from "@/lib/utils/utils";
import Flex from "../core/flex";
import { Link } from "@/i18n/routing";
import { ClientServiceDetailsResponse } from "@/types/service";
import Text from "./text";
import useLocalizer from "@/hooks/useLocalizer";
import { Activity } from "react";
import { DayOfWeek } from "@/types/core";
import { Separator } from "../ui/separator";
import HeaderLink from "../sections/header/headerLink";
import DynamicIcon from "./dynamicIcon";
import CallBox from "./callBox";

interface Props {
  className?: string;
  image?: string;
  data: Pick<
    ClientServiceDetailsResponse,
    "client" | "socialMedia" | "periods" | "covers"
  >;
}

const ContactInfoBox: React.FC<Props> = ({
  className,
  image,
  data: { client, socialMedia, periods, covers },
}) => {
  const { t, isRtl } = useLocalizer();
  return (
    <div className={cn("lg:col-span-1 flex flex-col gap-4", className)}>
      <Flex
        direction="col"
        className="bg-white border-t-4 border-primary p-8 gap-6 shadow"
      >
        <Activity mode={client?.whatsAppMobileNumber ? "visible" : "hidden"}>
          <Flex direction="col" spacing={{ all: 4 }}>
            <Text as="span" className="lg:text-xl text-primary/70">
              {t("titles.sendMessageByWhatsapp")}
            </Text>
            <Link
              target="_blank"
              aria-label={t("titles.sendMessageByWhatsapp")}
              href={`https://wa.me/${client?.whatsAppMobileNumber}`}
              className="font-bold text-lg lg:text-xl hover:text-primary"
            >
              <Text bdi>
                {formatPhoneNumber(
                  client?.whatsAppMobileNumber ?? "",
                  "+XX (XXX) XXX-XXXX",
                )}
              </Text>
            </Link>
          </Flex>
        </Activity>
        <Activity mode={client?.email ? "visible" : "hidden"}>
          <Flex direction="col" spacing={{ all: 4 }}>
            <Text as="span" className="lg:text-xl text-primary/70">
              {t("titles.sendMessageByEmail")}
            </Text>
            <Link
              target="_blank"
              aria-label={t("titles.sendMessageByEmail")}
              href={`mailto:${client?.email}`}
              className="font-bold text-lg lg:text-xl hover:text-primary"
            >
              <Text bdi>{client?.email}</Text>
            </Link>
          </Flex>
        </Activity>
        <Activity mode={covers && covers?.length > 0 ? "visible" : "hidden"}>
          <Flex direction="col" spacing={{ all: 4 }}>
            <Text as="span" className="lg:text-xl text-primary/70">
              {t("titles.serviceCoverageAreas")}
            </Text>
            <ul className="list-disc list-inside [&>li]:mb-2 flex flex-row flex-wrap gap-2">
              {covers?.map((cover) => (
                <li title={isRtl ? cover.enName : cover.arName} key={cover.id}>
                  {isRtl ? cover.arName : cover.enName}
                </li>
              ))}
            </ul>
          </Flex>
        </Activity>
        <Activity mode={periods && periods?.length > 0 ? "visible" : "hidden"}>
          <Flex direction="col" spacing={{ all: 4 }}>
            <Text as="span" className="lg:text-xl text-primary/70">
              {t("titles.workingHours")}
            </Text>
            <ul className="list-disc list-outside [&>li]:mb-2 flex flex-col gap-2 mx-3">
              {periods?.map((period) => (
                <li key={period.id}>
                  <Flex direction="row">
                    <Text as="span">
                      {getDayName(period.fromDay as DayOfWeek, false, isRtl)}
                      {period?.toDay &&
                        ` - ${getDayName(period.toDay as DayOfWeek, false, isRtl)}`}
                    </Text>
                    <Text as="span" className="mx-2 text-sm lg:text-base">
                      (
                      {period.startTime &&
                        getPeriodTime(period.startTime, isRtl)}
                      {period.endTime &&
                        ` - ${getPeriodTime(period.endTime, isRtl)}`}
                      )
                    </Text>
                  </Flex>
                </li>
              ))}
            </ul>
          </Flex>
        </Activity>
        <Activity
          mode={socialMedia && socialMedia?.length > 0 ? "visible" : "hidden"}
        >
          <Separator />
          <Flex
            direction="row"
            flexWrap="wrap"
            items="center"
            spacing={{ all: 4 }}
          >
            {socialMedia?.map((social, index) => (
              <HeaderLink
                key={social?.id ?? index}
                href={social.linkHref ?? "#"}
                icon={<DynamicIcon name={social?.icon?.code ?? ""} />}
              />
            ))}
          </Flex>
        </Activity>
      </Flex>
      <CallBox phoneNumber={client?.mobileNumber ?? ""} image={image} />
    </div>
  );
};

export default ContactInfoBox;
